import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Checkbox, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import i18next from "i18next";
import React from "react";

import { JobRole } from "../../../../../design/job-roles";
import { formatDate } from "../../../../../utils/helpers";
import { SkillInShift } from "../../../../scheduler/ui/dialog/copy-shifts-dialog/types";

interface ShiftCardProps {
	shiftDateTime: string;
	shiftId: number;
	isValid: boolean;
	time: { start: string; end: string };
	workcenter?: string;
	locationName?: string;
	selectedShiftIds: Set<number>;
	onShiftToggle: (shiftId: number) => void;
}

const ShiftCard = ({
	shiftDateTime,
	shiftId,
	isValid,
	time,
	workcenter,
	locationName,
	selectedShiftIds,
	onShiftToggle,
}: ShiftCardProps) => {
	const isChecked = selectedShiftIds.has(shiftId);
	return (
		<Box
			p={1}
			m={0.5}
			onClick={(e) => { e.stopPropagation(); onShiftToggle(shiftId); }}
			sx={{
				border: "1px solid rgba(0,0,0,0.12)",
				borderRadius: "8px",
				width: "190px",
				cursor: "pointer",
				outline: "none",
				"&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
				"&:focus": { outline: "none" },
				"&:focus-visible": { outline: "none" },
			}}
		>
			{/* Row 1: date + checkbox */}
			<Box display='flex' alignItems='center' justifyContent='space-between'>
				<Typography
					variant='subtitle2'
					color={isValid ? "#666666" : "#9e9e9e"}
				>
					{formatDate({ dateStr: shiftDateTime, formatType: "SHORT_DATE" })}
				</Typography>
				<Checkbox
					checked={isChecked}
					onChange={() => onShiftToggle(shiftId)}
					size='small'
					sx={{ p: 0, ml: 0.5 }}
					onClick={(e) => e.stopPropagation()}
				/>
			</Box>

			{/* Row 2: time + warning */}
			<Box display='flex' alignItems='center'>
				<Typography
					variant='body2'
					color={isValid ? "#666666" : "#9e9e9e"}
					fontSize='14px'
					sx={{ mr: 0.5 }}
				>
					{time.start} - {time.end}
				</Typography>
				{!isValid && (
					<WarningIcon
						fontSize='small'
						color='warning'
						sx={{ borderRadius: "20px" }}
					/>
				)}
			</Box>

			{/* Row 3: workcenter */}
			{workcenter && (
				<Box display='flex' alignItems='center' mt={0.5} gap={0.5}>
					<FactoryOutlinedIcon
						sx={{ fontSize: "13px", color: "rgba(0,0,0,0.54)" }}
					/>
					<Typography
						sx={{
							fontSize: "12px",
							color: "rgba(0,0,0,0.6)",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{workcenter}
					</Typography>
				</Box>
			)}

			{/* Row 4: location */}
			{locationName && (
				<Box display='flex' alignItems='center' mt={0.25} gap={0.5}>
					<LocationOnOutlinedIcon
						sx={{ fontSize: "13px", color: "rgba(0,0,0,0.54)" }}
					/>
					<Typography
						sx={{
							fontSize: "12px",
							color: "rgba(0,0,0,0.6)",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{locationName}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export const getColumns = (
	selectedShiftIds: Set<number>,
	onShiftToggle: (shiftId: number) => void
): GridColDef[] => [
	{
		field: "id",
		headerName: i18next.t("scheduler_job-role"),
		sortable: true,
		width: 220,
		renderCell: (params) => {
			return (
				<Box display='flex' alignItems='flex-start'>
					<JobRole jobRole={params.row.jobRole} />
				</Box>
			);
		},
	},
	{
		field: "shifts",
		headerName: i18next.t("scheduler_shifts"),
		sortable: false,
		flex: 1,
		renderCell: (params) => {
			return (
				<Box display='flex' flexWrap='wrap' py={0.5}>
					{params.row.shiftSortedDetails.map(
						(detail: {
							shiftDateTime: string;
							shiftId: number;
							isValid: boolean;
							time: { start: string; end: string };
							workcenter?: string;
							locationName?: string;
						}) => (
							<ShiftCard
								key={detail.shiftId}
								shiftDateTime={detail.shiftDateTime}
								shiftId={detail.shiftId}
								isValid={detail.isValid}
								time={detail.time}
								workcenter={detail.workcenter}
								locationName={detail.locationName}
								selectedShiftIds={selectedShiftIds}
								onShiftToggle={onShiftToggle}
							/>
						)
					)}
				</Box>
			);
		},
	},
];

export const getColumnsBySkills = (
	selectedShiftIds: Set<number>,
	onShiftToggle: (shiftId: number) => void
): GridColDef[] => [
	{
		field: "skillName",
		headerName: i18next.t("scheduler_skills"),
		width: 220,
		renderCell: (params) => {
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "flex-start",
					}}
				>
					{params.row.skills.map((skill: SkillInShift) => (
						<Typography
							key={`${params.row.id}-${skill.skillId}`}
							sx={{
								fontFamily: "Roboto",
								fontSize: "14px",
								color: "rgba(0,0,0,0.87)",
								fontWeight: 400,
								lineHeight: "20px",
								letterSpacing: "0.17px",
							}}
						>
							{skill.skillName}
						</Typography>
					))}
				</Box>
			);
		},
	},
	{
		field: "shifts",
		headerName: i18next.t("scheduler_shifts"),
		flex: 1,
		renderCell: (params) => {
			return (
				<Box display='flex' flexWrap='wrap' py={0.5}>
					{params.row.shiftSortedDetails.map(
						(detail: {
							shiftDateTime: string;
							shiftId: number;
							isValid: boolean;
							time: { start: string; end: string };
							workcenter?: string;
							locationName?: string;
						}) => (
							<ShiftCard
								key={detail.shiftId}
								shiftDateTime={detail.shiftDateTime}
								shiftId={detail.shiftId}
								isValid={detail.isValid}
								time={detail.time}
								workcenter={detail.workcenter}
								locationName={detail.locationName}
								selectedShiftIds={selectedShiftIds}
								onShiftToggle={onShiftToggle}
							/>
						)
					)}
				</Box>
			);
		},
	},
];

// Keep static exports for backwards compatibility
export const columns = getColumns(new Set(), () => undefined);
export const columnsBySkills = getColumnsBySkills(new Set(), () => undefined);

export function getDayNumber(dayName: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
	const days: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
		sunday: 0,
		monday: 1,
		tuesday: 2,
		wednesday: 3,
		thursday: 4,
		friday: 5,
		saturday: 6,
	};
	return days[dayName?.toLowerCase()] ?? 1;
}
